import React, { useCallback } from 'react';
import { styled } from '@mui/material/styles';
import { RadioGroup, FormControlLabel, Radio, Typography } from '@mui/material';
import { NODE_UI, stopPropagation } from './nodeUiStyles';

/**
 * NodeRadioGroup — Radio button group for mode selection.
 * Compact, monotone styling for on-canvas config.
 *
 * Usage:
 *   <NodeRadioGroup
 *     label="Delay Type"
 *     value={delayType}
 *     onChange={(val) => setDelayType(val)}
 *     options={[
 *       { value: 'fixed', label: 'Fixed delay' },
 *       { value: 'until', label: 'Until specific time' },
 *       { value: 'placeholder', label: 'From placeholder' },
 *     ]}
 *   />
 */

const Label = styled(Typography)(({ theme }) => ({
  fontSize: NODE_UI.fontSize.label,
  fontWeight: NODE_UI.fontWeight.label,
  color: theme.palette.text.secondary,
  lineHeight: 1.4,
  marginBottom: '4px',
  userSelect: 'none',
}));

const StyledRadioGroup = styled(RadioGroup)({
  gap: '2px',
});

const StyledFormControlLabel = styled(FormControlLabel)(({ theme }) => ({
  marginLeft: '-4px',
  marginRight: 0,
  '& .MuiTypography-root': {
    fontSize: NODE_UI.fontSize.value,
    fontWeight: NODE_UI.fontWeight.value,
    color: theme.palette.text.primary,
    lineHeight: 1.4,
  },
  '& .MuiRadio-root': {
    padding: '4px',
    color: theme.palette.text.secondary,
    '&.Mui-checked': {
      color: theme.palette.text.primary,
    },
  },
}));

const StyledRadio = styled(Radio)({
  '& .MuiSvgIcon-root': {
    fontSize: 18,
  },
});

const Description = styled(Typography)(({ theme }) => ({
  fontSize: NODE_UI.fontSize.caption,
  color: theme.palette.text.secondary,
  marginLeft: '26px',
  marginTop: '-2px',
  lineHeight: 1.3,
}));

const NodeRadioGroup = React.memo(function NodeRadioGroup({
  label,
  value,
  onChange,
  options = [],
  row = false,
  disabled = false,
  sx,
}) {
  const handleChange = useCallback((e) => {
    onChange?.(e.target.value);
  }, [onChange]);

  return (
    <div onMouseDown={stopPropagation} onClick={stopPropagation} onPointerDown={stopPropagation}>
      {label && <Label>{label}</Label>}
      <StyledRadioGroup
        value={value ?? ''}
        onChange={handleChange}
        row={row}
        sx={sx}
      >
        {options.map((opt) => (
          <React.Fragment key={opt.value}>
            <StyledFormControlLabel
              value={opt.value}
              disabled={disabled || opt.disabled}
              control={<StyledRadio size="small" />}
              label={opt.label}
            />
            {opt.description && (
              <Description>{opt.description}</Description>
            )}
          </React.Fragment>
        ))}
      </StyledRadioGroup>
    </div>
  );
});

export default NodeRadioGroup;
